import StudentsPage from "./features/students/components/StudentsPage";
import TeacherInvitationPage from "./features/students/components/TeacherInvitationPage";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import AppInitializer from "./components/AppInitializer";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  EmailVerificationPage,
  Dashboard,
  ProfilePage,
  AnonymousSettingsPage,
  SubscriptionPage,
  UserSubscriptionPage,
  PracticePage,
  DictationPage,
  DictationSessionPage,
  DictionaryPage,
  AdaptiveStudyPage,
  WordsPage,
  StripeSuccessPage,
  StripeCancelPage,
} from "./components/LazyComponents";
import LoadingFallback from "./components/LoadingFallback";
import i18n from "./config/i18n";
import { appTheme } from "./config/theme";
import { NotificationProvider } from "./contexts/NotificationContext";
import OAuthCallbackPage from "./features/auth/pages/OAuthCallbackPage";
import LearningHomePage from "./features/learning/LearningHomePage";
import AuthPage from "./pages/AuthPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProtectedRoute from "./routes/ProtectedRoute";
import { captureCampaignAttribution } from "./utils/campaignAttribution";
import { logger } from "./utils/logger";

// Инициализация i18n
import "./App.css";

// Initialize Stripe
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",
);

function App(): React.ReactElement {
  const location = useLocation();
  const [i18nReady, setI18nReady] = useState(() => i18n.isInitialized);
  const { t } = useTranslation("common", { useSuspense: false });

  useEffect(() => {
    captureCampaignAttribution(location.search);
  }, [location.search]);

  logger.debug(
    "[App.tsx] RENDER, i18nReady:",
    i18nReady,
    "i18n.isInitialized:",
    i18n.isInitialized,
  );

  // Ждем полной инициализации i18next
  useEffect(() => {
    logger.debug(
      "[App.tsx] useEffect mounted, i18n.isInitialized:",
      i18n.isInitialized,
    );

    // Проверяем сразу при монтировании
    if (i18n.isInitialized) {
      logger.debug(
        "[App.tsx] i18n already initialized, setting i18nReady=true",
      );
      setI18nReady(true);
      return;
    }

    // Если не инициализирован, подписываемся на событие
    const handleInitialized = (): void => {
      logger.debug(
        "[App.tsx] i18n initialized event fired, setting i18nReady=true",
      );
      setI18nReady(true);
    };

    i18n.on("initialized", handleInitialized);

    // Дополнительная проверка через небольшой таймаут
    // на случай если событие уже произошло
    const timeoutId = setTimeout(() => {
      logger.debug(
        "[App.tsx] timeout check, i18n.isInitialized:",
        i18n.isInitialized,
      );
      if (i18n.isInitialized) {
        logger.debug(
          "[App.tsx] i18n initialized via timeout, setting i18nReady=true",
        );
        setI18nReady(true);
      }
    }, 100);

    return () => {
      logger.debug("[App.tsx] useEffect cleanup");
      i18n.off("initialized", handleInitialized);
      clearTimeout(timeoutId);
    };
  }, []); // FIXED: Removed i18nReady from dependencies to prevent infinite loop

  // Показываем загрузку пока i18next не готов
  if (!i18nReady) {
    return <LoadingFallback />;
  }

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <Elements stripe={stripePromise}>
          <NotificationProvider>
            <AppInitializer>
              <Suspense
                fallback={
                  <LoadingFallback message={t("loading", "Loading...")} />
                }
              >
                <Routes>
                  {/* Лендинг вынесен на lingoshade.com — неавторизованные → /auth */}
                  <Route path="/" element={<Navigate to="/auth" replace />} />
                  <Route
                    path="/quickstart"
                    element={<Navigate to="/auth" replace />}
                  />

                  {/* Публичные пользовательские маршруты */}
                  <Route path="/auth" element={<AuthPage />} />
                  <Route
                    path="/login"
                    element={<Navigate to="/auth" replace />}
                  />
                  <Route
                    path="/register"
                    element={<Navigate to="/auth" replace />}
                  />
                  <Route
                    path="/register-multi"
                    element={<Navigate to="/auth" replace />}
                  />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />
                  <Route
                    path="/verify-email"
                    element={<EmailVerificationPage />}
                  />
                  {/* OAuth callback handler */}
                  <Route
                    path="/oauth/callback"
                    element={<OAuthCallbackPage />}
                  />

                  {/* Защищенные пользовательские маршруты */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="home" element={<LearningHomePage />} />
                    <Route path="texts" element={<Dashboard />} />
                    <Route path="students" element={<StudentsPage />} />
                    <Route
                      path="teacher-invitations/:token"
                      element={<TeacherInvitationPage />}
                    />
                    <Route path="dictation" element={<DictationPage />} />
                    <Route
                      path="dictation/:sessionId"
                      element={<DictationSessionPage />}
                    />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route
                      path="settings"
                      element={<AnonymousSettingsPage />}
                    />
                    <Route path="subscription" element={<SubscriptionPage />} />
                    <Route
                      path="subscription/manage"
                      element={<UserSubscriptionPage />}
                    />
                    <Route path="practice/:textId" element={<PracticePage />} />

                    {/* Dictionary routes */}
                    <Route path="dictionary" element={<DictionaryPage />} />
                    <Route
                      path="dictionary/adaptive"
                      element={<AdaptiveStudyPage />}
                    />
                    <Route path="dictionary/words" element={<WordsPage />} />

                    <Route
                      path="stripe/success"
                      element={<StripeSuccessPage />}
                    />
                    <Route
                      path="stripe/cancel"
                      element={<StripeCancelPage />}
                    />
                  </Route>

                  {/* Редирект на главную страницу для неизвестных маршрутов */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </AppInitializer>
          </NotificationProvider>
        </Elements>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
